import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { categoriesService } from '../../services/metalguard';
import { useSubscriptionStatus, SUB_STATUS } from '../../hooks/useSubscriptionStatus';
import SubscriptionBanner from 'components/SubscriptionBanner';

import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import { SkeletonCard, SkeletonRow, SkeletonChart } from '../../components/skeletons';

import SalesKPICards from './components/SalesKPICards';
import SalesTable from './components/SalesTable';
import MarginCharts from './components/MarginCharts';
import YieldCalibrationPanel from './components/YieldCalibrationPanel';
import BuyerPerformancePanel from './components/BuyerPerformancePanel';
import DiscountCalibrationPanel from './components/DiscountCalibrationPanel';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  { id: 'kpis', label: 'KPIs' },
  { id: 'table', label: 'Ventas' },
  { id: 'charts', label: 'Márgenes' },
  { id: 'yield', label: 'Yield' },
  { id: 'buyers', label: 'Compradores' },
  { id: 'discounts', label: 'Descuentos' },
];

function calcKPIs(sales) {
  if (!sales?.length) return { totalRevenue: 0, totalMargin: 0, avgMarginPerTon: 0, avgYieldAccuracy: 0, totalTonsSold: 0, avgDaysToSell: 0 };

  const totalRevenue = sales?.reduce((s, r) => s + (parseFloat(r?.total_sale_value) || 0), 0);
  const totalMargin = sales?.reduce((s, r) => s + (parseFloat(r?.realized_pnl) || 0), 0);
  const totalWeight = sales?.reduce((s, r) => s + (parseFloat(r?.weight_t) || 0), 0);
  const avgMarginPerTon = totalWeight > 0 ? totalMargin / totalWeight : 0;

  const yieldSales = sales?.filter(s => s?.yield_est && s?.actual_yield_pct);
  const avgYieldAccuracy = yieldSales?.length > 0
    ? yieldSales?.reduce((s, r) => {
        const est = parseFloat(r?.yield_est);
        const real = parseFloat(r?.actual_yield_pct);
        const accuracy = est > 0 ? (1 - Math.abs(real - est) / est) * 100 : 100;
        return s + accuracy;
      }, 0) / yieldSales?.length
    : null;

  const totalTonsSold = totalWeight;

  const daysSales = sales?.filter(s => s?.days_in_inventory !== null && s?.days_in_inventory !== undefined);
  const avgDaysToSell = daysSales?.length > 0
    ? daysSales?.reduce((s, r) => s + (parseFloat(r?.days_in_inventory) || 0), 0) / daysSales?.length
    : null;

  return { totalRevenue, totalMargin, avgMarginPerTon, avgYieldAccuracy, totalTonsSold, avgDaysToSell };
}

/**
 * Normalize a stock_sales record + its allocations into the unified shape
 * that all sales-analysis components expect.
 */
function normalizeStockSale(ss, allocations) {
  const weight = parseFloat(ss?.weight_sold_t) || 0;
  const pricePerTon = parseFloat(ss?.price_per_ton) || 0;
  const totalSaleValue = parseFloat(ss?.total_value) || (weight * pricePerTon);

  // Compute weighted purchase cost from allocations
  let totalPurchaseCost = 0;
  let totalAllocatedWeight = 0;
  let weightedYieldEst = 0;
  let weightedYieldActual = 0;
  let hasYieldData = false;
  let earliestBatchCreatedAt = null;

  (allocations || [])?.forEach(alloc => {
    const allocWeight = parseFloat(alloc?.weight_allocated) || 0;
    const batch = alloc?.batch || {};
    const purchasePrice = parseFloat(batch?.purchase_price) || 0;
    totalPurchaseCost += purchasePrice * allocWeight;
    totalAllocatedWeight += allocWeight;

    const yieldEst = parseFloat(batch?.yield_est);
    const yieldActual = parseFloat(alloc?.batch_yield_actual ?? batch?.yield_actual);
    if (yieldEst > 0) {
      weightedYieldEst += yieldEst * allocWeight;
      hasYieldData = true;
    }
    if (yieldActual > 0) {
      weightedYieldActual += yieldActual * allocWeight;
    }

    // Track earliest batch creation date for days_in_inventory
    if (batch?.created_at) {
      const batchDate = new Date(batch?.created_at);
      if (!earliestBatchCreatedAt || batchDate < earliestBatchCreatedAt) {
        earliestBatchCreatedAt = batchDate;
      }
    }
  });

  const avgYieldEst = hasYieldData && totalAllocatedWeight > 0 ? weightedYieldEst / totalAllocatedWeight : null;
  const avgYieldActual = hasYieldData && totalAllocatedWeight > 0 ? weightedYieldActual / totalAllocatedWeight : null;

  const realizedPnl = totalSaleValue - totalPurchaseCost;

  let daysInInventory = null;
  if (earliestBatchCreatedAt && ss?.sold_at) {
    const soldDate = new Date(ss?.sold_at);
    daysInInventory = Math.round((soldDate - earliestBatchCreatedAt) / (1000 * 60 * 60 * 24));
  }

  return {
    id: `ss_${ss?.id}`,
    _source: 'stock_sales',
    _original_id: ss?.id,
    user_id: ss?.user_id,
    batch_id: null,
    batch_ref: null,
    metal_name: ss?.metal_name,
    category_name: ss?.category_name,
    weight_t: weight,
    sale_price_per_ton: pricePerTon,
    total_sale_value: totalSaleValue,
    purchase_value: totalPurchaseCost,
    realized_pnl: realizedPnl,
    buyer_name: ss?.buyer_name || null,
    notes: ss?.notes || null,
    sold_at: ss?.sold_at,
    created_at: ss?.created_at,
    yield_est: avgYieldEst,
    actual_yield_pct: avgYieldActual,
    days_in_inventory: daysInInventory,
    risk_buffer: 0,
    negotiated_price: null,
  };
}

export default function SalesAnalysis() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [activeSection, setActiveSection] = useState('kpis');

  // Drill-down filter state
  const [externalFilters, setExternalFilters] = useState({});

  const handleDrillDown = useCallback(({ type, value }) => {
    setExternalFilters(prev => ({ ...prev, [type]: value }));
    setTimeout(() => {
      const el = document?.getElementById('section-table');
      if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    setActiveSection('table');
  }, []);

  const handleFilterByCategory = useCallback((category) => {
    handleDrillDown({ type: 'category', value: category });
  }, [handleDrillDown]);

  const handleFilterByBuyer = useCallback((buyer) => {
    handleDrillDown({ type: 'buyer', value: buyer });
  }, [handleDrillDown]);

  const handleClearExternalFilter = useCallback((key) => {
    setExternalFilters(prev => {
      const next = { ...prev };
      delete next?.[key];
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [salesRes, stockSalesRes, catRes] = await Promise.all([
        // Legacy sales table (MarkAsSoldModal flow)
        supabase
          ?.from('sales')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.order('sold_at', { ascending: false }),
        // New stock-sales flow — join allocations + batch data for cost/yield computation
        supabase
          ?.from('stock_sales')
          ?.select(`
            *,
            stock_sale_allocations (
              id,
              weight_allocated,
              batch_yield_actual,
              batch:batches (
                id,
                purchase_price,
                yield_est,
                yield_actual,
                created_at,
                metal_name,
                category_name
              )
            )
          `)
          ?.eq('user_id', user?.id)
          ?.order('sold_at', { ascending: false }),
        categoriesService?.getAll(),
      ]);

      const legacySales = salesRes?.error ? [] : (salesRes?.data || []);

      // Normalize stock_sales into the unified shape
      const normalizedStockSales = (stockSalesRes?.error ? [] : (stockSalesRes?.data || []))
        ?.map(ss => normalizeStockSale(ss, ss?.stock_sale_allocations));

      // Merge both sources, sorted by sold_at descending
      const merged = [...legacySales, ...normalizedStockSales]
        ?.sort((a, b) => {
          const da = a?.sold_at ? new Date(a?.sold_at) : new Date(0);
          const db = b?.sold_at ? new Date(b?.sold_at) : new Date(0);
          return db - da;
        });

      setSales(merged);
      if (!catRes?.error) setCategories(catRes?.data || []);
      setLastSync(new Date());
    } catch (err) {
      console.error('Error loading sales data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const handleRealtimeEvent = useCallback(() => {
    // On any change to either table, reload all data to keep merged list consistent
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time subscription on sales table
  const { status: subStatus, subWarningBanner, retryNow } = useSubscriptionStatus({
    channelName: `sales_realtime_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'sales',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: handleRealtimeEvent,
    onReconnect: loadData,
  });

  // Real-time subscription on stock_sales table
  useSubscriptionStatus({
    channelName: `stock_sales_realtime_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'stock_sales',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: handleRealtimeEvent,
    onReconnect: loadData,
  });

  const kpis = useMemo(() => calcKPIs(sales), [sales]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document?.getElementById(`section-${id}`);
    if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <main className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-6 py-6">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                <Link to="/main-dashboard" className="hover:text-foreground transition-colors">Dashboard</Link>
                <Icon name="ChevronRight" size={12} color="currentColor" />
                <span className="text-foreground font-medium">Análisis de Ventas</span>
              </nav>
              <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontWeight: 700 }}>Análisis de Ventas</h1>
              <p className="text-sm text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Rendimiento de márgenes y calibración de modelos de precio</p>
            </div>
            <div className="flex items-center gap-3">
              {lastSync && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    subStatus === SUB_STATUS?.CONNECTED ? 'bg-emerald-500 animate-pulse' :
                    subStatus === SUB_STATUS?.FAILED ? 'bg-red-500' : 'bg-amber-400 animate-pulse'
                  }`} />
                  Actualizado {lastSync?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <Icon name="RefreshCw" size={14} color="currentColor" />
                Sincronizar
              </button>
            </div>
          </div>

          <SubscriptionBanner status={subStatus} subWarningBanner={subWarningBanner} retryNow={retryNow} />

          {/* Section nav pills */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-6 scrollbar-hide">
            {SECTIONS?.map(sec => (
              <button
                key={sec?.id}
                onClick={() => scrollToSection(sec?.id)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  activeSection === sec?.id
                    ? 'text-white' : 'text-muted-foreground bg-muted hover:text-foreground'
                }`}
                style={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  background: activeSection === sec?.id ? 'var(--color-accent)' : undefined,
                }}
              >
                {sec?.label}
              </button>
            ))}
          </div>

          {/* KPI Cards */}
          <div id="section-kpis" className="mb-6 scroll-mt-20" data-tour="sales-kpis">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {Array.from({ length: 6 })?.map((_, i) => (
                  <SkeletonCard key={i} height="100px" />
                ))}
              </div>
            ) : (
              <SalesKPICards kpis={kpis} loading={false} />
            )}
          </div>

          {/* Sales Table */}
          <div id="section-table" className="mb-6 scroll-mt-20">
            {loading ? (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border">
                  <SkeletonCard height="28px" width="180px" rounded="rounded-md" />
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-border">
                    {Array.from({ length: 8 })?.map((_, i) => (
                      <SkeletonRow key={i} cols={8} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <SalesTable
                sales={sales}
                loading={loading}
                onNavigateInventory={() => navigate('/inventory')}
                externalFilters={externalFilters}
                onClearExternalFilter={handleClearExternalFilter}
              />
            )}
          </div>

          {/* Margin Charts */}
          <div id="section-charts" className="mb-6 scroll-mt-20">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="BarChart2" size={16} color="var(--color-accent)" strokeWidth={2} />
              <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Análisis de Márgenes</h2>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SkeletonChart height="200px" />
                <SkeletonChart height="200px" />
              </div>
            ) : (
              <MarginCharts sales={sales} loading={false} onDrillDown={handleDrillDown} />
            )}
          </div>

          {/* Yield Calibration */}
          <div id="section-yield" className="mb-6 scroll-mt-20" data-tour="sales-yield-calibration">
            {loading ? (
              <div className="bg-card border border-border rounded-xl p-4">
                <SkeletonCard height="28px" width="200px" rounded="rounded-md" className="mb-3" />
                <table className="w-full">
                  <tbody>
                    {Array.from({ length: 4 })?.map((_, i) => (
                      <SkeletonRow key={i} cols={5} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <YieldCalibrationPanel sales={sales} loading={false} onFilterByCategory={handleFilterByCategory} />
            )}
          </div>

          {/* Buyer Performance + Discount Calibration */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div id="section-buyers" className="scroll-mt-20">
              {loading ? (
                <div className="bg-card border border-border rounded-xl p-4">
                  <SkeletonCard height="28px" width="180px" rounded="rounded-md" className="mb-3" />
                  <table className="w-full">
                    <tbody>
                      {Array.from({ length: 4 })?.map((_, i) => (
                        <SkeletonRow key={i} cols={4} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <BuyerPerformancePanel sales={sales} loading={false} onFilterByBuyer={handleFilterByBuyer} />
              )}
            </div>
            <div id="section-discounts" className="scroll-mt-20">
              {loading ? (
                <div className="bg-card border border-border rounded-xl p-4">
                  <SkeletonCard height="28px" width="180px" rounded="rounded-md" className="mb-3" />
                  <table className="w-full">
                    <tbody>
                      {Array.from({ length: 4 })?.map((_, i) => (
                        <SkeletonRow key={i} cols={4} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <DiscountCalibrationPanel sales={sales} categories={categories} loading={false} />
              )}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
